<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { slide } from "svelte/transition";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import { soldInCases, soldInUnits } from "@shared/svelte/ProductHelpers";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import MoneyFormatters from "@shared/filters/money";
  import { groupBy } from "lodash";
  import PriceGroupDescription from "./PriceGroupDescription.svelte";
  import SpecialDiscountsTooltip from "@retailer/svelte/ProductDetails/Components/SpecialDiscountsTooltip.svelte";
  import { InventoryDiscountsStore } from "@retailer/svelte/stores/InventoryDiscountsStore";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  export let priceChanges: any = [];

  const mapPriceChangeArrows = changesByTier =>
    changesByTier.reduce((accumulator, change) => {
      if (change.price_change === "up") {
        accumulator[change.future_price_id] = "UpArrow";
      } else if (change.price_change === "down") {
        accumulator[change.future_price_id] = "DownArrow";
      }

      return accumulator;
    }, {});

  let priceChangeArrows = mapPriceChangeArrows(priceChanges);

  $: {
    priceChangeArrows = mapPriceChangeArrows(priceChanges);
  }

  export let product: Product;
  export let productLine: ProductLine;
  export let prices: any;

  function formatMoney(price) {
    return MoneyFormatters.formatWithDecimal(price);
  }

  function getFrontlinePricingData() {
    return prices.find(p => p.bottles == 1);
  }

  $: inventoryItemSoldBy = $InventoryItemStore?.inventoryItems[product.inventory_id]?.sold_by;

  function buildDealString(price) {
    const containerCaseSize = product.container_case_size || 1;
    const isSoldInCases = soldInCases(product, productLine, inventoryItemSoldBy);
    const isWholeCase = price.bottles % containerCaseSize === 0;

    const quantity =
      isSoldInCases && isWholeCase ? price.bottles / containerCaseSize : price.bottles;
    const quantityUnitName = isSoldInCases && isWholeCase ? "case" : product.container_type;

    const discountValue = isSoldInCases ? price.discount_per_case : price.discount_per_unit;
    const priceValue = isSoldInCases ? price.price_per_case : price.price_per_unit;
    const priceUnitName = isSoldInCases ? "case" : product.container_type;

    let dealString = `Buy ${quantity} ${pluralize(quantityUnitName, quantity)} \u2013 ${formatMoney(
      priceValue,
    )}/${priceUnitName} (${formatMoney(discountValue)} off)`;

    if (isSoldInCases && soldInUnits(product, productLine, inventoryItemSoldBy)) {
      dealString += ` \u2013 ${formatMoney(price.price_per_unit)}/${product.container_type}`;
    }

    return dealString;
  }

  function getDeals() {
    const pricing = prices.filter(p => p.bottles > 1).sort((a, b) => a.bottles - b.bottles);

    return pricing.map(p => {
      let parsedDeals = {
        dealString: buildDealString(p),
        future_price_id: p.future_price_id,
        priceChangeArrow: priceChangeArrows[p.future_price_id],
        price_group: p.price_group,
        price_type: p.price_type,
      };

      return parsedDeals;
    });
  }

  const frontlinePriceString = frontlinePrice => {
    let priceString = `${formatMoney(frontlinePrice.price_per_unit)}/${product.container_type}`;
    if (soldInCases(product, productLine, inventoryItemSoldBy)) {
      priceString += ` \u2013 ${formatMoney(frontlinePrice.price_per_case)}/case`;
    }
    return priceString;
  };

  const frontlinePrice = getFrontlinePricingData();

  const frontlinePriceChangeArrow =
    frontlinePrice && priceChangeArrows[frontlinePrice.future_price_id];
  const deals = getDeals();

  let dealsWithRequirementsDiscounts = deals.filter(deal => deal.price_group);
  const quantityDeals = deals.filter(
    deal => !deal.price_group && (deal.price_type === "QUANTITY" || !deal.price_type),
  );
  const mixMatchDeals = deals.filter(deal => !deal.price_group && deal.price_type === "MIX_MATCH");

  const storeMixDeals = $InventoryDiscountsStore.getDiscountsForInventoryId(
    product.inventory_id,
  ).mixDeals;

  if (dealsWithRequirementsDiscounts.length > 0) {
    dealsWithRequirementsDiscounts = Object.entries(
      groupBy(dealsWithRequirementsDiscounts, d => d.price_group.price_group_id),
    );
  }
</script>

{#if frontlinePrice}
  <div class="fizz-flex-row-0 fizz-stack-12 fizz-text-medium fizz-text-strong fizz-text-deal">
    <span>
      {frontlinePriceString(frontlinePrice)}
      {#if frontlinePriceChangeArrow}
        <Icon
          type={frontlinePriceChangeArrow}
          extraClassNames="fizz-text-brand fizz-inline-block"
          id={`${frontlinePriceChangeArrow}-fp_id_${frontlinePrice.future_price_id}`}
          dataTestId={`priceChange-${frontlinePriceChangeArrow}`}
          extraStyles="vertical-align: middle"
        />
      {/if}
    </span>
  </div>
{/if}
<div>
  {#if quantityDeals.length > 0 || mixMatchDeals.length > 0}
    {#if quantityDeals.length > 0}
      <p class="fizz-text-strong">Quantity Deals</p>
      <div class="fizz-stack-8" data-testid="quantity-deals-section">
        {#each quantityDeals as deal}
          <p>
            {deal.dealString}
            {#if deal.priceChangeArrow}
              <Icon
                type={deal.priceChangeArrow}
                extraClassNames="fizz-text-brand fizz-text-medium fizz-inline-block"
                id={`${deal.priceChangeArrow}-fp_id_${deal.future_price_id}`}
                dataTestId={`priceChange-${deal.priceChangeArrow}`}
                extraStyles="vertical-align: text-top"
              />
            {/if}
          </p>
        {/each}
      </div>
    {/if}
    {#if mixMatchDeals.length > 0}
      <p class="fizz-text-strong">Mix Deals</p>
      <div class="fizz-stack-8" data-testid="quantity-deals-section">
        {#if storeMixDeals.length > 0}
          {#if storeMixDeals[0].discounts[0].product_group_id}
            <UIRouterLink
              stateName="layout.deals_v2"
              params={{
                distributorId: product.distributor_id,
                productGroupId: storeMixDeals[0].discounts[0].product_group_id,
              }}
              trackingParams={{
                discountClick: true,
                product_id: storeMixDeals[0].discounts[0].product_id,
                distributor_id: product.distributor_id,
                product_group_id: storeMixDeals[0].discounts[0].product_group_id,
                sku: storeMixDeals[0].discounts[0].sku,
              }}
            >
              {storeMixDeals[0].dealName}
              {storeMixDeals[0].dealLabel === "item" ? "" : storeMixDeals[0].dealLabel} mix deal
            </UIRouterLink>
          {:else}
            <UIRouterLink
              stateName="layout.deal_detail"
              classes="nounderline-hover"
              params={{
                dealId: storeMixDeals[0].discounts[0].deal_id,
                dealTierId: storeMixDeals[0].discounts[0].deal_tier_id,
              }}
              trackingParams={{
                discountClick: true,
                product_id: storeMixDeals[0].discounts[0].product_id,
                distributor_id: storeMixDeals[0].discounts[0].distributor_id,
                deal_tier_id: storeMixDeals[0].discounts[0].deal_tier_id,
                sku: storeMixDeals[0].discounts[0].sku,
              }}
            >
              {storeMixDeals[0].dealName}
              {storeMixDeals[0].dealLabel === "item" ? "" : storeMixDeals[0].dealLabel} mix deal
            </UIRouterLink>
          {/if}
        {/if}
        {#each mixMatchDeals as deal}
          <p>
            {deal.dealString}
            {#if deal.priceChangeArrow}
              <Icon
                type={deal.priceChangeArrow}
                extraClassNames="fizz-text-brand fizz-text-medium fizz-inline-block"
                id={`${deal.priceChangeArrow}-fp_id_${deal.future_price_id}`}
                dataTestId={`priceChange-${deal.priceChangeArrow}`}
                extraStyles="vertical-align: text-top"
              />
            {/if}
          </p>
        {/each}
      </div>
    {/if}
  {:else if !frontlinePrice}
    <p>No prices available</p>
  {:else if !dealsWithRequirementsDiscounts.length}
    <p>No deals available</p>
  {/if}
  {#if dealsWithRequirementsDiscounts.length > 0}
    <div
      class="fizz-card fizz-text-small fizz-radius-medium"
      data-testid="special-discounts-section"
      transition:slide={{ delay: 0, duration: 200 }}
      style="overflow: visible"
    >
      <SpecialDiscountsTooltip horizontalAlignment="left" />

      {#each dealsWithRequirementsDiscounts as [id, discounts], i}
        <div data-testid="special-discount-group">
          <p class="fizz-text-strong">
            {discounts[0].price_group.name || "Special Discount"}
          </p>
          <PriceGroupDescription description={discounts[0].price_group.description || ""} />
          {#each discounts as discount}
            <p>
              {discount.dealString}
              {#if discount.priceChangeArrow}
                <Icon
                  type={discount.priceChangeArrow}
                  extraClassNames="fizz-text-brand fizz-text-medium fizz-inline-block"
                  id={`${discount.priceChangeArrow}-fp_id_${discount.future_price_id}`}
                  extraStyles="vertical-align: text-top"
                />
              {/if}
            </p>
          {/each}
          {#if i != dealsWithRequirementsDiscounts.length - 1}
            <p class="fizz-stack-16" />
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
